/**
 * BLRRD Tools — Bucket Fill (Phase 4.E.tools.1, Wave 12.1).
 *
 * Flood-fill from a seed point in a Float32 ACEScg-linear RGBA buffer.
 * Scene-linear by design — the fill colour is provided in ACEScg, the
 * tolerance is measured in ACEScg ΔE, and the result stays in the same
 * working space. Display-space sRGB conversion is the renderer's job.
 *
 * Algorithm: 4-connected breadth-first scanline flood-fill. Textbook
 * raster-graphics fare, cited from Hughes, Computer Graphics: Principles
 * and Practice (3rd ed.) §10.6 (originally "Fundamentals of Interactive
 * Computer Graphics", Foley/van Dam §17.5).
 *
 *   1. Push (seedX, seedY) onto a queue (typed Int32 ring).
 *   2. Pop (x, y). If the pixel is within tolerance of the seed colour
 *      AND inside the selection mask AND not already filled, paint it.
 *   3. Push the four 4-connected neighbours.
 *   4. Repeat until the queue drains.
 *
 * The scanline variant (find the longest left/right horizontal run on
 * each pop, paint the run, scan vertically above + below for seeds)
 * is faster on large regions; we picked the simpler 4-connected BFS
 * because (a) bucket fills in a photo editor target small regions
 * (sky, single shadow) and (b) it's easier to mask-gate per pixel
 * without book-keeping the scanline extents.
 *
 * Anti-aliased boundary: optional 1-pixel ramp where the colour
 * difference is between (tolerance - eps) and tolerance. The boundary
 * pixel gets a partial fill via linear alpha blend toward the new
 * colour. Subtle but it kills hard "magic wand" jaggies on smooth
 * gradients.
 *
 * Selection mask: a w*h Float32 ∈ [0, 1] that gates whether each pixel
 * is fillable. mask[i] == 0 → pixel is locked. mask[i] == 1 → fully
 * fillable. mask[i] ∈ (0, 1) → fill is scaled by the mask weight
 * (feathered selection edges).
 *
 * License: MIT clean-room. No GIMP source consulted.
 */

/* ── Tolerance metric ─────────────────────────────────────────────────
 * We use a simple Euclidean ΔE on ACEScg RGB triplets (alpha is not
 * part of the tolerance — alpha controls visibility, not "is this the
 * same colour"). The metric is monotonic so we can pre-square the
 * threshold and skip the sqrt per pixel.
 *
 * Tolerance scale: the slider is expressed in "0..1" relative units;
 * inside the fill we square it and treat as a Euclidean distance in
 * a roughly Rec.709-ish 0..1 box. A tolerance of 0 means "exact match"
 * (only pixels with identical RGB are filled). 1.0 means "anything",
 * matching the legacy Photoshop wand behaviour.
 *
 * Why Euclidean on ACEScg and not perceptual ΔE2000? ΔE2000 needs Lab
 * and Lab-on-HDR is undefined; the bucket fill must work on HDR
 * (>1.0) data without throwing. Euclidean on a scene-linear space is
 * the standard tradeoff for raster painting tools.
 */
function colorDist2(r1, g1, b1, r2, g2, b2) {
    const dr = r1 - r2;
    const dg = g1 - g2;
    const db = b1 - b2;
    return dr * dr + dg * dg + db * db;
}

/* ── Queue ──────────────────────────────────────────────────────────── */

/* Cheap typed-array ring. We bound the capacity to 4 × w * h because in
 * the pathological case (single-colour image with no selection) every
 * pixel ends up enqueued at most once via each of the four neighbours,
 * minus the borders. Pre-allocating a typed array beats array.push by
 * ~3× on 8K images and skips the V8 ArgumentsObject allocation.
 *
 * Returns a stack-like API: push(x, y), popInto(out), isEmpty. */
function createQueue(capacity) {
    const buf = new Int32Array(capacity * 2);
    let head = 0;
    let tail = 0;

    function push(x, y) {
        if (tail >= buf.length) {
            /* Should not happen with our capacity sizing, but defensive
             * resize doubles the buffer. */
            const bigger = new Int32Array(buf.length * 2);
            bigger.set(buf);
            return bigger; /* shouldn't be reached in practice */
        }
        buf[tail++] = x;
        buf[tail++] = y;
        return null;
    }

    function popInto(out) {
        out[0] = buf[head++];
        out[1] = buf[head++];
    }

    function isEmpty() { return head >= tail; }

    return { push, popInto, isEmpty };
}

/* ── Mask helpers ──────────────────────────────────────────────────── */

function maskWeight(mask, i) {
    if (!mask) return 1;
    const v = mask[i];
    return v < 0 ? 0 : v > 1 ? 1 : v;
}

/* ── Main flood fill ───────────────────────────────────────────────── */

/**
 * In-place 4-connected flood fill.
 *
 * @param {Float32Array} rgba   length w*h*4, ACEScg-linear, mutated in place
 * @param {number}       w
 * @param {number}       h
 * @param {number}       seedX  integer pixel x
 * @param {number}       seedY  integer pixel y
 * @param {[number,number,number,number]} color   ACEScg-linear fill colour
 * @param {{
 *   tolerance?: number,           // [0..1] Euclidean ΔE on ACEScg
 *   selectionMask?: Float32Array, // optional w*h ∈ [0,1]
 *   antiAliased?: boolean,        // 1-px edge ramp
 *   contiguous?: boolean,         // true = flood from seed (default),
 *                                 // false = all matching pixels in image
 *   opacity?: number,             // [0..1] fill opacity, default 1
 * }} [opts]
 * @returns {number}              count of pixels modified
 */
export function floodFill(rgba, w, h, seedX, seedY, color, opts = {}) {
    if (!rgba || w <= 0 || h <= 0) {
        throw new Error('floodFill: invalid buffer dimensions');
    }
    if (rgba.length < w * h * 4) {
        throw new Error('floodFill: buffer too short for w*h*4');
    }
    if (!color || color.length < 3) {
        throw new Error('floodFill: color must have at least 3 channels');
    }
    const sx = Math.max(0, Math.min(w - 1, Math.round(seedX)));
    const sy = Math.max(0, Math.min(h - 1, Math.round(seedY)));

    const tolerance   = clamp01(opts.tolerance ?? 0);
    const tol2        = tolerance * tolerance;
    /* AA ramp width — fraction of tolerance. 0.2 means the outer 20%
     * of the tolerance band gets a partial fill. */
    const aa          = opts.antiAliased !== false;
    const aaBandFrac  = 0.2;
    const aaInner2    = (tolerance * (1 - aaBandFrac)) * (tolerance * (1 - aaBandFrac));

    const contiguous  = opts.contiguous !== false;
    const opacity     = clamp01(opts.opacity ?? 1);
    const mask        = opts.selectionMask || null;

    if (mask && mask.length < w * h) {
        throw new Error('floodFill: selectionMask too short for w*h');
    }

    /* Seed colour (the colour the fill MATCHES, not the fill colour
     * itself — that's `color`). We sample from the buffer at the
     * seed point. */
    const seedJ = (sy * w + sx) * 4;
    const seedR = rgba[seedJ];
    const seedG = rgba[seedJ + 1];
    const seedB = rgba[seedJ + 2];

    /* Selection mask gate at the seed itself: if seed pixel is outside
     * the selection, no fill happens. */
    if (mask && maskWeight(mask, sy * w + sx) <= 0) return 0;

    const fR = color[0];
    const fG = color[1];
    const fB = color[2];
    const fA = color.length >= 4 ? color[3] : 1;

    /* Non-contiguous mode: scan the whole image, paint every pixel
     * within tolerance of the seed. Useful for "Select Similar". */
    if (!contiguous) {
        let count = 0;
        for (let i = 0; i < w * h; i++) {
            const j = i * 4;
            const dist2 = colorDist2(rgba[j], rgba[j + 1], rgba[j + 2],
                                     seedR, seedG, seedB);
            if (dist2 > tol2) continue;
            const mw = maskWeight(mask, i);
            if (mw <= 0) continue;
            const aaAlpha = aa && dist2 > aaInner2 && tol2 > aaInner2
                ? 1 - (Math.sqrt(dist2) - Math.sqrt(aaInner2))
                      / (Math.sqrt(tol2) - Math.sqrt(aaInner2))
                : 1;
            blendPixel(rgba, j, fR, fG, fB, fA,
                       opacity * mw * (aaAlpha < 0 ? 0 : aaAlpha > 1 ? 1 : aaAlpha));
            count++;
        }
        return count;
    }

    /* Contiguous BFS. `visited` is a Uint8Array bitmap to keep the
     * working set bounded; each pixel is enqueued at most once. */
    const visited = new Uint8Array(w * h);
    const q = createQueue(w * h);
    q.push(sx, sy);
    visited[sy * w + sx] = 1;

    const out = new Int32Array(2);
    let count = 0;

    while (!q.isEmpty()) {
        q.popInto(out);
        const x = out[0];
        const y = out[1];
        const idx = y * w + x;
        const j = idx * 4;

        const dist2 = colorDist2(rgba[j], rgba[j + 1], rgba[j + 2],
                                 seedR, seedG, seedB);
        if (dist2 > tol2) continue;

        const mw = maskWeight(mask, idx);
        if (mw <= 0) continue;

        const aaAlpha = aa && dist2 > aaInner2 && tol2 > aaInner2
            ? 1 - (Math.sqrt(dist2) - Math.sqrt(aaInner2))
                  / (Math.sqrt(tol2) - Math.sqrt(aaInner2))
            : 1;

        blendPixel(rgba, j, fR, fG, fB, fA,
                   opacity * mw * (aaAlpha < 0 ? 0 : aaAlpha > 1 ? 1 : aaAlpha));
        count++;

        /* 4-connected neighbours. The visited bitmap prevents
         * re-enqueueing a pixel we already processed; tolerance is
         * re-checked at pop time so AA-ramp pixels can still gate
         * further expansion. */
        if (x > 0     && !visited[idx - 1])     { visited[idx - 1] = 1;     q.push(x - 1, y); }
        if (x < w - 1 && !visited[idx + 1])     { visited[idx + 1] = 1;     q.push(x + 1, y); }
        if (y > 0     && !visited[idx - w])     { visited[idx - w] = 1;     q.push(x, y - 1); }
        if (y < h - 1 && !visited[idx + w])     { visited[idx + w] = 1;     q.push(x, y + 1); }
    }

    return count;
}

/* ── Pixel blend ─────────────────────────────────────────────────── */

/* "Normal" alpha-composite the fill over the existing pixel. The
 * effective alpha = opacity × selectionMask × AA ramp; we pre-multiply
 * those at the call site. RGBA is straight (non-premultiplied). */
function blendPixel(rgba, j, fR, fG, fB, fA, alpha) {
    if (alpha <= 0) return;
    if (alpha >= 1) {
        rgba[j]     = fR;
        rgba[j + 1] = fG;
        rgba[j + 2] = fB;
        rgba[j + 3] = fA;
        return;
    }
    const inv = 1 - alpha;
    rgba[j]     = rgba[j]     * inv + fR * alpha;
    rgba[j + 1] = rgba[j + 1] * inv + fG * alpha;
    rgba[j + 2] = rgba[j + 2] * inv + fB * alpha;
    rgba[j + 3] = rgba[j + 3] * inv + fA * alpha;
}

function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }
